import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, Shield, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { HTTP_FORBIDDEN } from "@/lib/api";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [need2FA, setNeed2FA] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { email, password };
      if (need2FA) payload.code = code;
      const res = await api.post("/auth/login", payload);
      localStorage.setItem("gf_token", res.data.token);
      localStorage.setItem("gf_user", JSON.stringify(res.data.user));
      toast.success("Welcome back!");
      navigate("/dashboard");
      window.location.reload();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === HTTP_FORBIDDEN && detail === "2FA code required") {
        setNeed2FA(true);
        toast.info("Enter your 6-digit code from your authenticator app");
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSubmitLabel = () => {
    if (loading) return <Loader2 className="w-4 h-4 animate-spin"/>;
    if (need2FA) return <>Verify & sign in <ArrowRight className="w-4 h-4 ml-2"/></>;
    return <>Sign in <ArrowRight className="w-4 h-4 ml-2"/></>;
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-[45%] bg-[#1C3F35] text-white p-12 flex-col justify-between relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2.5 relative z-10" data-testid="login-brand">
          <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-lg tracking-tight">Beacon of Grants</span>
        </Link>
        <div className="relative z-10">
          <div className="label-eyebrow text-white/60">Welcome back</div>
          <h2 className="font-heading font-light text-4xl mt-4 leading-tight">
            The pipeline<br/>doesn't stop.<br/>
            <span className="text-[#C1654B]">Neither do you.</span>
          </h2>
          <p className="text-white/70 mt-6 max-w-md">
            Sign in to pick up drafts, review matches, and hit the next deadline.
          </p>
        </div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#C1654B]/20 blur-3xl"/>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8" data-testid="login-brand-mobile">
            <div className="w-8 h-8 rounded-md bg-[#1C3F35] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-lg">Beacon of Grants</span>
          </Link>

          <div className="label-eyebrow mb-3">{need2FA ? "Two-factor" : "Sign in"}</div>
          <h1 className="font-heading font-light text-4xl tracking-tighter text-[#1A1A1A]">
            {need2FA ? "Enter your code" : "Sign in to your account"}
          </h1>
          <p className="text-[#5E5E5E] mt-2">
            {need2FA ? (
              <>Open your authenticator app for the current 6-digit code.</>
            ) : (
              <>Don't have one yet? <Link to="/signup" className="text-[#1C3F35] underline underline-offset-4" data-testid="login-to-signup">Create an account</Link></>
            )}
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            {!need2FA && (
              <>
                <GoogleSignInButton testId="login-google" />
                <div className="flex items-center gap-3 my-2">
                  <div className="h-px flex-1 bg-[#E6E4DF]"/>
                  <span className="text-[10px] uppercase tracking-widest text-[#7A8C77] font-bold">or with email</span>
                  <div className="h-px flex-1 bg-[#E6E4DF]"/>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email" type="email" required
                    value={email} onChange={(e)=>setEmail(e.target.value)}
                    data-testid="login-email"
                    className="mt-2 bg-white border-[#E6E4DF]"
                    placeholder="jane@nonprofit.org"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm font-medium text-[#1C3F35] underline underline-offset-4 hover:text-[#C1654B] transition-colors"
                      data-testid="login-forgot-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative mt-2">
                    <Input
                      id="password" type={showPassword ? "text" : "password"} required
                      value={password} onChange={(e)=>setPassword(e.target.value)}
                      data-testid="login-password"
                      className="bg-white border-[#E6E4DF] pr-10"
                    />
                    <button
                      type="button"
                      onClick={()=>setShowPassword((s)=>!s)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      data-testid="login-password-toggle"
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5E5E5E] hover:text-[#1C3F35] transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                  </div>
                </div>
              </>
            )}

            {need2FA && (
              <div>
                <div className="flex items-center gap-2 mb-2 text-[#1C3F35]">
                  <Shield className="w-4 h-4"/>
                  <span className="text-sm font-medium">Two-factor code</span>
                </div>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="login-2fa-code"
                  className="mt-2 bg-white border-[#E6E4DF] text-center text-2xl tracking-[0.4em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => { setNeed2FA(false); setCode(""); }}
                  data-testid="login-2fa-back"
                  className="mt-3 text-xs text-[#5E5E5E] hover:text-[#1C3F35]"
                >
                  ← Back
                </button>
              </div>
            )}

            <Button
              type="submit" disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#1C3F35] text-white hover:bg-[#1A362D] rounded-md py-6"
            >
              {renderSubmitLabel()}
            </Button>
            {!need2FA && (
              <p className="text-center text-sm text-[#5E5E5E]">
                Can't sign in?{" "}
                <Link
                  to="/forgot-password"
                  className="font-medium text-[#1C3F35] underline underline-offset-4 hover:text-[#C1654B] transition-colors"
                  data-testid="login-forgot-link-secondary"
                >
                  Reset your password
                </Link>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
